import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function WelcomePopup() {
  const { i18n, t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');
  const [cookieAccepted, setCookieAccepted] = useState(false);

  useEffect(() => {
    const hasVisited = localStorage.getItem('welcomeShown');
    if (!hasVisited) {
      setIsVisible(true);
    }
  }, []);

  const languages = [
    { code: 'en', name: t('ui.language_english') || 'English', flag: '🇬🇧' },
    { code: 'es', name: t('ui.language_spanish') || 'Español', flag: '🇪🇸' },
    { code: 'it', name: t('ui.language_italian') || 'Italiano', flag: '🇮🇹' },
    { code: 'nl', name: t('ui.language_dutch') || 'Nederlands', flag: '🇳🇱' },
  ];

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    i18n.changeLanguage(langCode);
  };

  const handleAccept = () => {
    if (cookieAccepted) {
      localStorage.setItem('welcomeShown', 'true');
      localStorage.setItem('cookieAccepted', 'true');
      localStorage.setItem('language', selectedLanguage);
      setIsVisible(false);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.4, type: 'spring', stiffness: 100 }}
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
              borderRadius: '16px',
              padding: '2.5rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 119, 190, 0.15)',
              border: '1px solid rgba(0, 119, 190, 0.1)',
            }}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                style={{
                  fontSize: 'clamp(22px, 5vw, 28px)',
                  fontWeight: 700,
                  color: '#0077BE',
                  marginBottom: '0.5rem',
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                {t('welcome.title')}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                style={{
                  fontSize: '14px',
                  color: '#666',
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                {t('welcome.subtitle')}
              </motion.p>
            </div>

            {/* Language Selection */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              style={{ marginBottom: '2rem' }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#0077BE',
                  marginBottom: '1rem',
                  fontFamily: 'Outfit, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {t('welcome.selectLanguage')}
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0.75rem',
                }}
              >
                {languages.map((lang, index) => (
                  <motion.button
                    key={lang.code}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.05, duration: 0.3 }}
                    onClick={() => handleLanguageChange(lang.code)}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border:
                        selectedLanguage === lang.code
                          ? '2px solid #FF9500'
                          : '2px solid #E0E0E0',
                      background:
                        selectedLanguage === lang.code
                          ? 'linear-gradient(135deg, #FF9500, #FFB347)'
                          : '#FFFFFF',
                      color:
                        selectedLanguage === lang.code ? '#FFFFFF' : '#333',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Outfit, sans-serif',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.35rem',
                    }}
                    onHover
                  >
                    <span style={{ fontSize: '1.5rem' }}>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Cookie Consent */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              style={{
                background: 'rgba(0, 119, 190, 0.05)',
                borderLeft: '4px solid #0077BE',
                padding: '1rem',
                borderRadius: '6px',
                marginBottom: '2rem',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#333',
                  fontFamily: 'Outfit, sans-serif',
                  lineHeight: '1.4',
                }}
              >
                <input
                  type="checkbox"
                  checked={cookieAccepted}
                  onChange={(e) => setCookieAccepted(e.target.checked)}
                  style={{
                    marginTop: '2px',
                    minWidth: '18px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#FF9500',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: '#0077BE',
                      marginBottom: '0.25rem',
                    }}
                  >
                    {t('welcome.cookieConsent')}
                  </div>
                  <div style={{ color: '#666' }}>
                    {t('welcome.cookieText')}
                    <a
                      href="/cookies"
                      style={{
                        color: '#0077BE',
                        textDecoration: 'none',
                        fontWeight: 600,
                        marginLeft: '0.3rem',
                      }}
                    >
                      {t('welcome.learnMore')}
                    </a>
                  </div>
                </div>
              </label>
            </motion.div>

            {/* Button */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              onClick={handleAccept}
              disabled={!cookieAccepted}
              style={{
                width: '100%',
                padding: '1rem',
                background: cookieAccepted
                  ? 'linear-gradient(135deg, #FF9500, #FFB347)'
                  : '#CCCCCC',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 700,
                fontFamily: 'Outfit, sans-serif',
                cursor: cookieAccepted ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: cookieAccepted
                  ? '0 8px 20px rgba(255, 149, 0, 0.25)'
                  : 'none',
              }}
              onMouseEnter={(e) => {
                if (cookieAccepted) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 28px rgba(255, 149, 0, 0.35)';
                }
              }}
              onMouseLeave={(e) => {
                if (cookieAccepted) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 20px rgba(255, 149, 0, 0.25)';
                }
              }}
            >
              {t('welcome.acceptButton')}
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
